import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import FileUpload from "@/components/FileUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, FileText, CheckCircle2, XCircle, Send, Lock, Briefcase, Clock, CalendarRange, Hourglass, Pencil, Bug, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { toSimpleStatus, SimpleStatusBadge, DueHoursCountdown } from "@/pages/Tasks";
import IssueDrawer from "@/components/IssueDrawer";
import RichTextEditor from "@/components/RichTextEditor";
import TesterDeviceInput from "@/components/TesterDeviceInput";
import ReviewAllSubmissionsDialog from "@/components/ReviewAllSubmissionsDialog";
import AssignMoreTestersDialog from "@/components/AssignMoreTestersDialog";
import { QRCodeSVG } from "qrcode.react";

// Detect legacy plain-text descriptions vs new HTML from the rich editor.
const looksLikeHtml = (s) => typeof s === "string" && /<\/?[a-z][\s\S]*>/i.test(s);

function fmtBoundary(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return `${date}, ${time}`;
}

/** ISO -> "YYYY-MM-DDTHH:MM" suitable for <input type="datetime-local"> (no seconds, local tz). */
function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TASK_TYPES = ["Exploratory", "Functional", "Regression", "UI/UX", "Security"];
const TASK_STATUSES = ["Draft", "Assigned", "In Progress", "Submitted", "Accepted", "Rejected", "Paid"];

export default function TaskDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [openSubmit, setOpenSubmit] = useState(false);
  const [openReviewAll, setOpenReviewAll] = useState(false);
  const [openAssignMore, setOpenAssignMore] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [edit, setEdit] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [approvedTesters, setApprovedTesters] = useState([]);
  const [submit, setSubmit] = useState({ submission_notes: "", evidence_files: [] });
  const [openDecline, setOpenDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [declineBusy, setDeclineBusy] = useState(false);
  const [rating, setRating] = useState({ value: 0, notes: "", existing: null });
  const [linkedIssues, setLinkedIssues] = useState([]);
  const [groupSummary, setGroupSummary] = useState(null);
  const [drawerIssueId, setDrawerIssueId] = useState(null);
  const [openIssue, setOpenIssue] = useState(false);
  const [issueBusy, setIssueBusy] = useState(false);
  const [issueForm, setIssueForm] = useState({
    title: "",
    severity: "medium",
    steps_to_reproduce: "",
    expected_result: "",
    actual_result: "",
    environment: "",
    device_browser: "",
    attachments: [],
  });

  const load = async () => {
    const { data } = await api.get(`/tasks/${id}`);
    setT(data);
    // Fetch group summary in the background — only admins/managers see the inline card.
    (async () => {
      try {
        if (user && (user.role === "admin" || user.role === "manager")) {
          const { data: rs } = await api.get(`/tasks/${id}/review-summary`);
          setGroupSummary(rs);
        }
      } catch { /* silently skip */ }
    })();
    return data;
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const loadLinkedIssues = async () => {
    try {
      const { data } = await api.get(`/issues`, { params: { task_id: id } });
      setLinkedIssues(data || []);
    } catch { setLinkedIssues([]); }
  };
  useEffect(() => { loadLinkedIssues(); /* eslint-disable-next-line */ }, [id]);

  const resetIssueForm = () => setIssueForm({
    title: "", severity: "medium", steps_to_reproduce: "", expected_result: "",
    actual_result: "", environment: "", device_browser: "", attachments: [],
  });

  const onIssueAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Attachment exceeds 10MB"); e.target.value = ""; return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIssueForm(f => ({ ...f, attachments: [...f.attachments, { id: data.id, name: data.name, mime: data.mime }] }));
      toast.success("Attached");
    } catch { toast.error("Upload failed"); }
    finally { e.target.value = ""; }
  };

  const submitIssue = async () => {
    if (!issueForm.title.trim() || issueForm.title.trim().length < 5) { toast.error("Add a short, descriptive title"); return; }
    if (!issueForm.steps_to_reproduce.trim()) { toast.error("Add steps to reproduce"); return; }
    setIssueBusy(true);
    try {
      await api.post("/issues", {
        project_id: t.project_id,
        task_id: id,
        title: issueForm.title.trim(),
        severity: issueForm.severity,
        priority: issueForm.severity,
        steps_to_reproduce: issueForm.steps_to_reproduce.trim(),
        expected_result: issueForm.expected_result.trim(),
        actual_result: issueForm.actual_result.trim(),
        environment: issueForm.environment.trim() || t.test_environment || "",
        device_browser: issueForm.device_browser.trim() || t.device_requirements || "",
        attachments: issueForm.attachments,
        status: "New",
      });
      toast.success("Issue reported and linked to this task");
      setOpenIssue(false);
      resetIssueForm();
      loadLinkedIssues();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not report issue");
    } finally { setIssueBusy(false); }
  };

  // Load my (manager/admin) prior rating for this task so the stars are pre-filled.
  useEffect(() => {
    if (!t || (user.role !== "admin" && user.role !== "manager")) return;
    (async () => {
      try {
        const { data } = await api.get(`/tasks/${id}/ratings`);
        const mine = (data || []).find(r => r.manager_id === user.id);
        if (mine) setRating({ value: mine.rating, notes: mine.notes || "", existing: mine });
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [t?.id]);

  // (Auto-accept-on-mount removed in Phase 3A — tester must explicitly accept.)

  if (!t) return <AppLayout title="Loading..."><div>Loading…</div></AppLayout>;

  const isMine = user.role === "freelancer" && t.assigned_to === user.id;
  const canEdit = user.role === "admin" || user.role === "manager";
  const canReviewAll = (user.role === "admin" || user.role === "manager") && ["Submitted", "Accepted", "Approved", "Rejected", "Paid"].includes(t.status);
  const simple = toSimpleStatus(t.status);
  const locked = simple === "Completed";

  const acceptInvite = async () => {
    try { await api.post(`/tasks/${id}/accept`); toast.success("Task accepted — ready to start"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not accept"); }
  };
  const declineInvite = async () => {
    setDeclineBusy(true);
    try {
      const reason = (declineReason || "").trim().slice(0, 280);
      const { data } = await api.post(`/tasks/${id}/decline`, reason ? { reason } : {});
      toast.success(data.reassigned_to ? "Declined — task re-assigned" : "Declined — admin notified to re-route");
      setOpenDecline(false);
      setDeclineReason("");
      nav("/tasks");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not decline"); }
    finally { setDeclineBusy(false); }
  };
  const startTesting = async () => {
    try { await api.post(`/tasks/${id}/start`); toast.success("Started — clock is now ticking"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not start"); }
  };

  const submitTask = async () => {
    try {
      await api.post(`/tasks/${id}/submit`, submit);
      toast.success("Submitted");
      setOpenSubmit(false);
      load();
    } catch { toast.error("Failed"); }
  };

  const submitRating = async () => {
    if (!rating.value) { toast.error("Pick a star rating first"); return; }
    try {
      const { data } = await api.post(`/tasks/${id}/rate`, { rating: rating.value, notes: rating.notes });
      toast.success(`Rated ${rating.value}/5 — tester score is now ${data.tester_score} (${data.level})`);
      setRating(r => ({ ...r, existing: { ...(r.existing||{}), rating: rating.value, notes: rating.notes } }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save rating");
    }
  };

  const startEdit = async () => {
    setEdit({
      title: t.title || "",
      description: t.description || "",
      task_type: t.task_type || "Functional",
      test_environment: t.test_environment || "",
      device_requirements: t.device_requirements || "",
      allocated_hours: t.allocated_hours ?? 4,
      payout_amount: t.payout_amount ?? 0,
      start_at: toLocalInput(t.start_at),
      due_date: toLocalInput(t.due_date),
      assigned_to: t.assigned_to ? [t.assigned_to] : [],
      status: t.status || "Draft",
    });
    setOpenEdit(true);
    if (approvedTesters.length === 0) {
      try {
        const { data } = await api.get("/users", { params: { role: "freelancer", status: "approved" } });
        setApprovedTesters(data || []);
      } catch { /* swallow */ }
    }
  };
  const saveEdit = async () => {
    if (!edit.title.trim()) { toast.error("Title is required"); return; }
    setEditBusy(true);
    try {
      const baseFields = {
        title: edit.title,
        description: edit.description,
        task_type: edit.task_type,
        test_environment: edit.test_environment,
        device_requirements: edit.device_requirements,
        allocated_hours: Number(edit.allocated_hours),
        payout_amount: Number(edit.payout_amount),
        start_at: edit.start_at ? new Date(edit.start_at).toISOString() : null,
        due_date: edit.due_date ? new Date(edit.due_date).toISOString() : null,
      };
      const assignees = edit.assigned_to || [];

      // Always patch the current task first.
      const primaryAssignee = assignees[0] || null;
      await api.patch(`/tasks/${id}`, {
        ...baseFields,
        assigned_to: primaryAssignee,
        status: edit.status,
      });

      // For additional assignees, clone the task (one task per extra tester) reusing
      // the new field values + status "Assigned" so they show up in each tester's queue.
      const extras = assignees.slice(1).filter(uid => uid !== t.assigned_to);
      if (extras.length > 0) {
        const projectId = t.project_id;
        const results = await Promise.allSettled(
          extras.map(uid => api.post("/tasks", {
            ...baseFields,
            project_id: projectId,
            assigned_to: uid,
            status: "Assigned",
          }))
        );
        const ok = results.filter(r => r.status === "fulfilled").length;
        if (ok < extras.length) {
          toast.error(`${extras.length - ok} clone task${extras.length - ok > 1 ? "s" : ""} could not be created`);
        }
        toast.success(`Updated current task + created ${ok} clone task${ok === 1 ? "" : "s"}`);
      } else {
        toast.success("Task updated");
      }
      setOpenEdit(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update task");
    } finally { setEditBusy(false); }
  };

  return (
    <AppLayout title={t.title} subtitle={`Task · ${t.id.slice(0,8).toUpperCase()}`}>
      <button onClick={()=>nav("/tasks")} className="text-sm text-slate-500 hover:text-slate-900 mb-3 flex items-center gap-1.5" data-testid="btn-back-tasks"><ArrowLeft className="w-4 h-4"/> Tasks</button>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="kpi-card" data-testid="task-detail-card">
            <div className="flex items-center justify-between mb-3">
              {isMine ? (
                <SimpleStatusBadge value={simple} testid="task-detail-simple-status"/>
              ) : (
                <StatusBadge kind="task" value={t.status} />
              )}
              <div className="text-xs text-slate-500">Created {new Date(t.created_at).toLocaleString()}</div>
            </div>
            <h2 className="font-display text-2xl font-bold text-slate-900">{t.title}</h2>
            {t.description && (
              <div className="mt-2">
                <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-1">Task Instruction</div>
                {looksLikeHtml(t.description) ? (
                  <div
                    className="prose prose-sm max-w-none text-slate-700"
                    data-testid="task-instruction"
                    // Content is authored by admins/managers only and is rendered here for read-only display.
                    dangerouslySetInnerHTML={{ __html: t.description }}
                  />
                ) : (
                  <p className="text-slate-600 whitespace-pre-wrap" data-testid="task-instruction">{t.description}</p>
                )}
              </div>
            )}

            {/* 6 metric fields */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5 pt-4 border-t border-slate-100 text-sm" data-testid="task-metrics">
              <Metric icon={Briefcase} label="Project Name" testid="metric-project">{t.project_name || "—"}</Metric>
              <Metric icon={Briefcase} label="Task Type" testid="metric-type">{t.task_type || "Functional"}</Metric>
              <Metric icon={Clock} label="Total Allocated" testid="metric-allocated">{(t.allocated_hours ?? 0)} Hours Total</Metric>
              <Metric icon={Hourglass} label="Due Hours" testid="metric-due-hours">
                <DueHoursCountdown endIso={t.due_date} completed={locked}/>
              </Metric>
              <Metric icon={CalendarRange} label="Due Timeline" className="col-span-2 md:col-span-2" testid="metric-timeline">
                <span className="text-slate-900 font-medium">{fmtBoundary(t.start_at || t.created_at)} — {fmtBoundary(t.due_date)}</span>
              </Metric>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div><div className="text-[11px] uppercase text-slate-500">Environment</div><div className="font-medium text-slate-900">{t.test_environment || "—"}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">Devices</div><div className="font-medium text-slate-900">{t.device_requirements || "—"}</div></div>
              <div><div className="text-[11px] uppercase text-slate-500">Payout</div><div className="font-mono text-slate-900">${(t.payout_amount||0).toFixed(2)}</div></div>
            </div>

            {isMine && locked && (
              <div className="mt-4 flex items-center gap-2 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="locked-banner">
                <Lock className="w-4 h-4"/> Task is locked — submission received. No further edits allowed.
              </div>
            )}
          </div>

          {t.build_files?.length > 0 && (
            <div className="kpi-card" data-testid="task-build-files">
              <h3 className="font-display font-bold text-slate-900 mb-1">Client build</h3>
              <p className="text-xs text-slate-500 mb-3">Download from your desktop, or scan the QR code on your phone to install directly.</p>
              <ul className="space-y-3">
                {t.build_files.map((b, i) => {
                  const relPath = b.url?.startsWith("/") ? b.url : `/api/files/${b.id}`;
                  const isAbsolute = b.url?.startsWith("http://") || b.url?.startsWith("https://");
                  const absoluteUrl = isAbsolute
                    ? b.url
                    : `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${relPath}`;
                  return (
                    <li key={i} className="flex items-center gap-3 border border-slate-200 rounded-md p-3 hover:border-cyan-400 transition-colors" data-testid={`task-build-row-${i}`}>
                      <div className="shrink-0 bg-white p-1.5 border border-slate-200 rounded" data-testid={`task-build-qr-${i}`}>
                        <QRCodeSVG value={absoluteUrl} size={80} level="M" includeMargin={false}/>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">{b.name}</div>
                        <div className="text-[11px] text-slate-500">
                          {b.size ? `${(b.size / (1024*1024)).toFixed(1)} MB` : ""}{b.mime ? ` · ${b.mime}` : ""}
                        </div>
                        <a
                          href={absoluteUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-cyan-700 hover:text-cyan-900"
                          data-testid={`task-build-download-${i}`}
                        >
                          <FileText className="w-3.5 h-3.5"/> Download
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="text-[10px] text-slate-400 mt-3 leading-snug">
                iOS builds installed from a QR code require a signed development / TestFlight profile — plain <b>.ipa</b> files won&apos;t install directly on iOS. Android <b>.apk</b> installs work once &ldquo;Install unknown apps&rdquo; is enabled for your browser.
              </p>
            </div>
          )}

          {t.submission_notes && (
            <div className="kpi-card">
              <h3 className="font-display font-bold text-slate-900 mb-2">Submission</h3>
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{t.submission_notes}</p>
              {t.evidence_files?.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {t.evidence_files.map((e, i) => (
                    <a key={i} href={e.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-cyan-700 hover:underline border border-slate-200 rounded-md p-2" data-testid={`evidence-${i}`}>
                      <FileText className="w-4 h-4"/> {e.name}
                    </a>
                  ))}
                </div>
              )}
              {t.quality_score != null && (
                <div className="mt-3 text-sm">
                  <div className="text-[11px] uppercase text-slate-500">Quality score</div>
                  <div className="font-mono text-slate-900 text-lg">{t.quality_score}/100</div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="space-y-4">
          {canEdit && (
            <Button
              variant="outline"
              className="w-full"
              onClick={startEdit}
              data-testid="btn-edit-task"
            >
              <Pencil className="w-4 h-4 mr-1.5"/> Edit task
            </Button>
          )}
          {isMine && t.status === "Assigned" && (
            <div className="space-y-2" data-testid="tester-accept-decline">
              <Button onClick={acceptInvite} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-tester-accept">
                <CheckCircle2 className="w-4 h-4 mr-1.5"/> Accept task
              </Button>
              <Dialog open={openDecline} onOpenChange={(v) => { setOpenDecline(v); if (!v) setDeclineReason(""); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full text-red-600 hover:bg-red-50 border-red-200" data-testid="btn-tester-decline">
                    <XCircle className="w-4 h-4 mr-1.5"/> Decline
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="decline-dialog">
                  <DialogHeader>
                    <DialogTitle>Decline this task?</DialogTitle>
                    <DialogDescription>
                      It will be re-assigned to the next best available approved tester on this project.
                      Add a short reason so the project manager understands your decision (optional, max 280 chars).
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor="decline-reason">Reason (optional)</Label>
                    <Textarea
                      id="decline-reason"
                      rows={4}
                      maxLength={280}
                      placeholder="e.g. Out of office until next week / device unavailable / scheduling conflict"
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      data-testid="input-decline-reason"
                    />
                    <div className="text-xs text-slate-500 text-right">{declineReason.length}/280</div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpenDecline(false)} disabled={declineBusy} data-testid="btn-decline-cancel">Cancel</Button>
                    <Button onClick={declineInvite} disabled={declineBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirm-decline">
                      {declineBusy ? "Declining…" : "Confirm decline"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
          {isMine && t.status === "Ready" && (
            <Button onClick={startTesting} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="btn-start-testing">
              <Send className="w-4 h-4 mr-1.5"/> Start testing
            </Button>
          )}
          {isMine && t.status === "In Progress" && (
            <Dialog open={openIssue} onOpenChange={(v) => { setOpenIssue(v); if (!v) resetIssueForm(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="w-full border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="btn-report-issue">
                  <Bug className="w-4 h-4 mr-1.5"/> Report issue
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[85vh] overflow-auto" data-testid="report-issue-dialog">
                <DialogHeader>
                  <DialogTitle>Report a new issue</DialogTitle>
                  <DialogDescription>
                    The issue is automatically linked to this task. The project manager will be notified.
                    Add evidence screenshots / logs to speed up triage.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="issue-title">Title</Label>
                    <Input id="issue-title" maxLength={200}
                      placeholder="Short, descriptive — e.g. 'Sign-in CTA does nothing on Safari iOS 17'"
                      value={issueForm.title}
                      onChange={(e) => setIssueForm(f => ({ ...f, title: e.target.value }))}
                      data-testid="input-issue-title"/>
                  </div>
                  <div>
                    <Label htmlFor="issue-severity">Severity</Label>
                    <select id="issue-severity"
                      value={issueForm.severity}
                      onChange={(e) => setIssueForm(f => ({ ...f, severity: e.target.value }))}
                      className="w-full border border-slate-200 rounded h-9 px-2 text-sm"
                      data-testid="select-issue-severity">
                      <option value="critical">Critical — blocking, no workaround</option>
                      <option value="high">High — major impact, workaround exists</option>
                      <option value="medium">Medium — moderate impact</option>
                      <option value="low">Low — cosmetic / minor</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="issue-steps">Steps to reproduce</Label>
                    <Textarea id="issue-steps" rows={4}
                      placeholder={"1. Open the checkout page\n2. Tap Pay with card\n3. Observe…"}
                      value={issueForm.steps_to_reproduce}
                      onChange={(e) => setIssueForm(f => ({ ...f, steps_to_reproduce: e.target.value }))}
                      data-testid="input-issue-steps"/>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="issue-expected">Expected result</Label>
                      <Textarea id="issue-expected" rows={3}
                        placeholder="What should happen"
                        value={issueForm.expected_result}
                        onChange={(e) => setIssueForm(f => ({ ...f, expected_result: e.target.value }))}
                        data-testid="input-issue-expected"/>
                    </div>
                    <div>
                      <Label htmlFor="issue-actual">Actual result</Label>
                      <Textarea id="issue-actual" rows={3}
                        placeholder="What actually happens"
                        value={issueForm.actual_result}
                        onChange={(e) => setIssueForm(f => ({ ...f, actual_result: e.target.value }))}
                        data-testid="input-issue-actual"/>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="issue-env">Environment</Label>
                      <Input id="issue-env"
                        placeholder={t.test_environment || "Prod / Staging / URL"}
                        value={issueForm.environment}
                        onChange={(e) => setIssueForm(f => ({ ...f, environment: e.target.value }))}
                        data-testid="input-issue-env"/>
                    </div>
                    <div>
                      <Label htmlFor="issue-device">Device / browser</Label>
                      <TesterDeviceInput
                        placeholder={t.device_requirements || "iPhone 15 Pro, Safari 17.4"}
                        value={issueForm.device_browser}
                        onChange={(v) => setIssueForm(f => ({ ...f, device_browser: v }))}
                        testid="input-issue-device"/>
                    </div>
                  </div>
                  <div>
                    <Label>Attachments (screenshots / logs / video — ≤10MB each)</Label>
                    <input type="file" onChange={onIssueAttachment} accept="image/*,application/pdf,video/mp4,.log,.txt,.json"
                      className="block w-full text-xs text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                      data-testid="input-issue-attachment"/>
                    {issueForm.attachments.length > 0 && (
                      <ul className="mt-2 space-y-1" data-testid="issue-attachments-list">
                        {issueForm.attachments.map((a, idx) => (
                          <li key={a.id} className="flex items-center justify-between text-xs text-slate-600 bg-slate-50 rounded px-2 py-1">
                            <span className="truncate">{a.name}</span>
                            <button type="button" onClick={() => setIssueForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }))}
                              className="text-red-600 hover:text-red-700 ml-2" data-testid={`btn-remove-attachment-${idx}`}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenIssue(false)} disabled={issueBusy} data-testid="btn-cancel-issue">Cancel</Button>
                  <Button onClick={submitIssue} disabled={issueBusy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="btn-submit-issue">
                    {issueBusy ? "Reporting…" : "Report issue"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {isMine && t.status === "In Progress" && (
            <Dialog open={openSubmit} onOpenChange={setOpenSubmit}>
              <DialogTrigger asChild><Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="btn-submit-task"><Send className="w-4 h-4 mr-1.5"/> Submit task</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Submit task</DialogTitle><DialogDescription>Add a short summary of what was tested. Once submitted, the task is locked for further edits.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Submission notes</Label><Textarea rows={4} value={submit.submission_notes} onChange={e=>setSubmit(s=>({...s, submission_notes:e.target.value}))} data-testid="input-submission-notes"/></div>
                </div>
                <DialogFooter><Button onClick={submitTask} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-confirm-submit">Submit</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canReviewAll && !t.group_id && (
            <Button
              onClick={() => setOpenReviewAll(true)}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-review-all-submissions"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5"/> Review all submissions
            </Button>
          )}
          {canEdit && !t.group_id && (
            <Button
              variant="outline"
              onClick={() => setOpenAssignMore(true)}
              className="w-full border-slate-300"
              data-testid="btn-assign-more-testers"
            >
              <Bug className="w-4 h-4 mr-1.5"/> Assign more testers
            </Button>
          )}
          {t.group_id && (user.role === "admin" || user.role === "manager") && (
            <Link to={`/task-groups/${t.group_id}`} className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-cyan-300 bg-cyan-50 text-cyan-900 hover:bg-cyan-100 text-sm font-medium px-3 py-2" data-testid="btn-open-task-group">
              Open task group view
            </Link>
          )}

          <div className="kpi-card">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Project</div>
            <div className="text-sm font-medium text-slate-900">{t.project_name || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">{t.client_name}</div>
          </div>

          {(user.role === "admin" || user.role === "manager") && groupSummary && groupSummary.submissions.length > 1 && !t.group_id && (
            <div className="kpi-card" data-testid="inline-group-summary">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase text-slate-500 flex items-center gap-1.5">
                  <Bug className="w-3 h-3"/> Testers on this task
                </div>
                <span className="text-slate-400 text-xs">{groupSummary.submissions.length}</span>
              </div>
              <ul className="space-y-1.5" data-testid="inline-group-submissions">
                {groupSummary.submissions.map(s => (
                  <li key={s.task_id} className="text-xs flex items-center justify-between gap-2 border border-slate-100 rounded p-2" data-testid={`inline-group-row-${s.task_id}`}>
                    <div className="min-w-0">
                      <Link to={`/tasks/${s.task_id}`} className={`font-medium truncate hover:text-cyan-700 ${s.task_id === id ? "text-cyan-700" : "text-slate-900"}`}>
                        {s.tester.name}{s.task_id === id ? " (this task)" : ""}
                      </Link>
                      <div className="text-[10px] text-slate-500">{s.status}{s.quality_score != null ? ` · ${s.quality_score}/100` : ""}</div>
                    </div>
                    {s.status !== "Assigned" && s.status !== "In Progress" && (
                      <div className={`text-[10px] font-mono px-2 py-0.5 rounded border ${s.is_low ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {s.submission_score}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {t.group_id && (
                <Link to={`/task-groups/${t.group_id}`} className="mt-2 inline-flex text-xs text-cyan-700 hover:text-cyan-900" data-testid="inline-group-open-link">
                  Open task group view →
                </Link>
              )}
            </div>
          )}

          {(isMine || user.role === "admin" || (user.role === "manager" && (t.manager_id === user.id || !t.manager_id))) && (
            <div className="kpi-card" data-testid="linked-issues-panel">
              <div className="text-[11px] uppercase text-slate-500 mb-2 flex items-center justify-between">
                <span className="flex items-center gap-1.5"><Bug className="w-3 h-3"/> Linked issues</span>
                <span className="text-slate-400">{linkedIssues.length}</span>
              </div>
              {linkedIssues.length === 0 ? (
                <div className="text-xs text-slate-500">
                  {isMine && t.status === "In Progress"
                    ? "Nothing reported yet. Tap Report issue above to log a bug while you test."
                    : "No issues linked to this task yet."}
                </div>
              ) : (
                <ul className="space-y-2" data-testid="linked-issues-list">
                  {linkedIssues.map(i => (
                    <li key={i.id} className="text-xs border border-slate-100 rounded p-2" data-testid={`linked-issue-${i.id}`}>
                      <button type="button" onClick={() => setDrawerIssueId(i.id)} className="text-left w-full" data-testid={`open-issue-drawer-${i.id}`}>
                        <div className="font-medium text-slate-900 line-clamp-2 hover:text-cyan-700">{i.title}</div>
                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-500">
                          <span className={`pill ${
                            i.severity === "critical" ? "bg-red-50 text-red-700 border-red-200" :
                            i.severity === "high" ? "bg-rose-50 text-rose-700 border-rose-200" :
                            i.severity === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
                            "bg-slate-50 text-slate-600 border-slate-200"
                          }`}>{i.severity || "—"}</span>
                          <span>·</span>
                          <span>{i.status}</span>
                          {i.suspected_duplicate_of && (
                            <span className="ml-auto inline-flex items-center gap-0.5 text-amber-700" title="LLM flagged as possible duplicate">
                              <AlertTriangle className="w-2.5 h-2.5"/> dup
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {(user.role === "admin" || user.role === "manager") && t.assigned_to && false && (
            <div className="kpi-card" data-testid="rate-tester-panel-deprecated">
              {/* Manager rating removed — scoring is now fully automated. */}
            </div>
          )}
        </div>
      </div>

      {/* Edit Task Dialog (admin/manager) */}
      <Dialog open={openEdit} onOpenChange={(o) => { if (!editBusy) setOpenEdit(o); }}>
        <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit task</DialogTitle>
            <DialogDescription>Update any field below. Changing the assignee or status will trigger the relevant notification.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="input-edit-title"/>
              </div>
              <div>
                <Label>Task Instruction</Label>
                <RichTextEditor
                  value={edit.description || ""}
                  onChange={(html) => setEdit({ ...edit, description: html })}
                  placeholder="Detailed instructions for testers…"
                  minHeight={180}
                  testid="input-edit-desc"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Task type</Label>
                  <Select value={edit.task_type} onValueChange={(v) => setEdit({ ...edit, task_type: v })}>
                    <SelectTrigger data-testid="select-edit-type"><SelectValue/></SelectTrigger>
                    <SelectContent>{TASK_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger data-testid="select-edit-status"><SelectValue/></SelectTrigger>
                    <SelectContent>{TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Allocated hours</Label>
                  <Input type="number" min={0} step={0.5} value={edit.allocated_hours} onChange={(e) => setEdit({ ...edit, allocated_hours: e.target.value })} inputMode="decimal" className="no-spinner" data-testid="input-edit-hours"/>
                </div>
                <div>
                  <Label>Payout (USD)</Label>
                  <Input type="number" min={0} step={1} value={edit.payout_amount} onChange={(e) => setEdit({ ...edit, payout_amount: e.target.value })} inputMode="decimal" className="no-spinner" data-testid="input-edit-payout"/>
                </div>
                <div>
                  <Label>Start</Label>
                  <Input type="datetime-local" value={edit.start_at} onChange={(e) => setEdit({ ...edit, start_at: e.target.value })} data-testid="input-edit-start"/>
                </div>
                <div>
                  <Label>Due</Label>
                  <Input type="datetime-local" value={edit.due_date} onChange={(e) => setEdit({ ...edit, due_date: e.target.value })} data-testid="input-edit-due"/>
                </div>
                <div>
                  <Label>Environment</Label>
                  <Input value={edit.test_environment} onChange={(e) => setEdit({ ...edit, test_environment: e.target.value })} data-testid="input-edit-env"/>
                </div>
                <div>
                  <Label>Device requirements</Label>
                  <Input value={edit.device_requirements} onChange={(e) => setEdit({ ...edit, device_requirements: e.target.value })} data-testid="input-edit-devices"/>
                </div>
              </div>
              <div>
                <Label>Assigned to</Label>
                <div className="mt-1 border border-slate-200 rounded-md max-h-48 overflow-auto" data-testid="edit-assignee-list">
                  {approvedTesters.length === 0 ? (
                    <div className="px-3 py-4 text-xs text-slate-500 text-center">Loading approved testers…</div>
                  ) : (
                    [...approvedTesters].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(u => {
                      const checked = (edit.assigned_to || []).includes(u.id);
                      return (
                        <label key={u.id} className={`flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 ${checked ? "bg-cyan-50/40" : ""}`} data-testid={`option-edit-assignee-${u.id}`}>
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => {
                              const set = new Set(edit.assigned_to || []);
                              if (set.has(u.id)) set.delete(u.id); else set.add(u.id);
                              setEdit({ ...edit, assigned_to: [...set] });
                            }}
                            data-testid={`checkbox-edit-assignee-${u.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                            <div className="text-[11px] text-slate-500 truncate">{u.email} · {u.country || "—"}</div>
                          </div>
                          {u.id === t.assigned_to && <span className="text-[10px] uppercase tracking-wider text-cyan-700 font-medium flex-shrink-0">Current</span>}
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-1.5">
                  {(edit.assigned_to || []).length === 0
                    ? "Pick one or more approved testers, or leave empty to unassign."
                    : (edit.assigned_to || []).length === 1
                      ? "This tester will own the task."
                      : `${(edit.assigned_to || []).length} testers — current task goes to the first; ${(edit.assigned_to || []).length - 1} additional clone task${(edit.assigned_to || []).length > 2 ? "s" : ""} will be created.`}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)} disabled={editBusy} data-testid="btn-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="btn-edit-save">
              {editBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <IssueDrawer
        issueId={drawerIssueId}
        onClose={() => setDrawerIssueId(null)}
        taskStatus={t.status}
        currentUser={user}
        onChange={loadLinkedIssues}
      />

      <ReviewAllSubmissionsDialog
        open={openReviewAll}
        onOpenChange={setOpenReviewAll}
        taskId={id}
        onAnyDecided={load}
      />

      <AssignMoreTestersDialog
        open={openAssignMore}
        onOpenChange={setOpenAssignMore}
        taskId={id}
        existingAssignees={(groupSummary?.submissions?.map(s => s.tester?.id).filter(Boolean)) || (t.assigned_to ? [t.assigned_to] : [])}
        onCloned={load}
      />
    </AppLayout>
  );
}

function Metric({ icon: Icon, label, children, testid, className = "" }) {
  return (
    <div className={className} data-testid={testid}>
      <div className="text-[10px] uppercase text-slate-500 tracking-wider flex items-center gap-1"><Icon className="w-3 h-3"/>{label}</div>
      <div className="font-medium text-slate-900 mt-0.5 text-sm">{children}</div>
    </div>
  );
}
